import React, { useState } from 'react';
import { Send, Mail, Phone, MapPin, Clock, Upload, X, FileText, Image as ImageIcon, Plus, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';

export const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    relocatingFrom: '',
    relocatingTo: '',
    message: '',
    acceptFee: false
  });
  
  // Multiple pets structure
  const [pets, setPets] = useState([
    { id: 1, name: '', type: '', files: [] }
  ]);
  
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handlePetChange = (petId, field, value) => {
    setPets(pets.map(pet => 
      pet.id === petId ? { ...pet, [field]: value } : pet
    ));
  };

  const addPet = () => {
    const newId = Math.max(...pets.map(p => p.id), 0) + 1;
    setPets([...pets, { id: newId, name: '', type: '', files: [] }]);
  };

  const removePet = (petId) => {
    if (pets.length > 1) {
      setPets(pets.filter(pet => pet.id !== petId));
    } else {
      toast({
        title: "Cannot remove",
        description: "At least one pet is required",
        variant: "destructive"
      });
    }
  };

  const handlePetFileChange = (petId, e) => {
    const files = Array.from(e.target.files);

    // Validate file size (25MB max per file)
    const maxSize = 25 * 1024 * 1024;
    const validFiles = files.filter(file => {
      if (file.size > maxSize) {
        toast({
          title: "File too large",
          description: `${file.name} exceeds 25MB limit`,
          variant: "destructive"
        });
        return false;
      }
      return true;
    });

    // Get current pet's files
    const pet = pets.find(p => p.id === petId);
    const currentFiles = pet?.files || [];

    // Limit to 5 files per pet — keep as many valid files as will fit
    const slotsLeft = Math.max(0, 5 - currentFiles.length);
    const toAdd = validFiles.slice(0, slotsLeft);
    if (validFiles.length > slotsLeft) {
      toast({
        title: "Some files skipped",
        description: `Maximum 5 files per pet. Added ${toAdd.length}; ignored ${validFiles.length - slotsLeft}.`,
        variant: "destructive"
      });
    }
    if (toAdd.length === 0) return;

    // Add files to specific pet
    setPets(pets.map(p =>
      p.id === petId
        ? { ...p, files: [...currentFiles, ...toAdd] }
        : p
    ));

    // Confirmation toast so the user knows the files are attached
    const petLabel = pet?.name?.trim() || pet?.type?.trim() || 'your pet';
    toast({
      title: `${toAdd.length} document${toAdd.length === 1 ? '' : 's'} attached`,
      description: `Added to ${petLabel}. They'll be uploaded when you submit the request.`,
    });
    // Reset input so selecting the same file again still fires onChange
    if (e && e.target) e.target.value = '';
  };

  const removePetFile = (petId, fileIndex) => {
    setPets(pets.map(pet => 
      pet.id === petId 
        ? { ...pet, files: pet.files.filter((_, i) => i !== fileIndex) }
        : pet
    ));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate at least one pet has information
    const validPets = pets.filter(pet => pet.name.trim() || pet.type.trim());
    if (validPets.length === 0) {
      toast({
        title: "Pet information required",
        description: "Please provide at least one pet's name or type",
        variant: "destructive"
      });
      return;
    }
    
    setUploading(true);
    setUploadProgress(0);
    
    try {
      // Create FormData for file upload
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('relocatingFrom', formData.relocatingFrom);
      formDataToSend.append('relocatingTo', formData.relocatingTo);
      formDataToSend.append('message', formData.message);
      formDataToSend.append('acceptFee', formData.acceptFee.toString());

      // Add pets information as JSON
      const petsData = pets.map(pet => ({
        name: pet.name,
        type: pet.type,
        fileCount: pet.files.length
      }));
      formDataToSend.append('pets', JSON.stringify(petsData));

      // Append files with pet association
      pets.forEach((pet, petIndex) => {
        pet.files.forEach((file) => {
          formDataToSend.append('files', file);
          formDataToSend.append(`file_pet_index`, petIndex.toString());
        });
      });

      setUploadProgress(30);

      // Send form data to backend
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/consultations/upload`, {
        method: 'POST',
        body: formDataToSend
      });

      setUploadProgress(80);

      if (!response.ok) {
        // Extract a helpful error from the backend response where possible
        let serverMessage = '';
        try {
          const errBody = await response.json();
          if (errBody?.detail) {
            serverMessage = Array.isArray(errBody.detail)
              ? errBody.detail.map(d => d.msg || d).join(', ')
              : String(errBody.detail);
          }
        } catch (_) { /* non-JSON response */ }
        throw new Error(serverMessage || `Server responded with ${response.status}`);
      }

      const data = await response.json();
      console.log('Consultation submitted:', data);

      setUploadProgress(100);

      const totalFiles = pets.reduce((sum, pet) => sum + pet.files.length, 0);
      const filesUploadedByServer = data?.files_uploaded ?? totalFiles;

      // 1) Documents uploaded notification (only if files were sent)
      if (filesUploadedByServer > 0) {
        toast({
          title: "Documents uploaded successfully",
          description: `${filesUploadedByServer} document${filesUploadedByServer === 1 ? '' : 's'} saved securely against your consultation record.`,
        });
      }

      // 2) Consultation submitted confirmation
      toast({
        title: "✓ Consultation request submitted",
        description: `Thank you, ${formData.name || 'we'}'ve received your request for ${pets.length} pet${pets.length === 1 ? '' : 's'}${filesUploadedByServer > 0 ? ` and ${filesUploadedByServer} document${filesUploadedByServer === 1 ? '' : 's'}` : ''}. Reference: ${(data?.consultation_id || '').slice(0, 8).toUpperCase()}. We'll email you within 24 hours to schedule your consultation (CI$50).`,
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        relocatingFrom: '',
        relocatingTo: '',
        message: '',
        acceptFee: false,
      });
      setPets([{ id: 1, name: '', type: '', files: [] }]);
      setUploadProgress(0);
    } catch (error) {
      console.error('Error submitting consultation:', error);
      toast({
        title: "Submission Failed",
        description: error?.message
          ? `${error.message}. Please try again or contact us directly.`
          : "There was an error submitting your request. Please try again or contact us directly.",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-b from-muted/30 to-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-primary/20 to-accent/20 border-2 border-primary/30 rounded-full px-5 py-2.5 mb-6 shadow-sm">
            <Send className="w-5 h-5 text-primary" />
            <span className="text-sm font-bold text-primary uppercase tracking-wide">Get In Touch</span>
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6">
            Let's Plan Your Pet's Journey
          </h2>
          <p className="text-base sm:text-lg text-foreground/70 leading-relaxed">
            Share your pet's details and travel plans. We'll get back to you with a personalized consultation and quote.
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1 space-y-8">
              <div>
                <h3 className="text-2xl font-bold text-foreground mb-6">
                  Contact Information
                </h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <div className="font-semibold text-foreground mb-1">Email</div>
                      <a href="mailto:pawsome.ky@gmail.com" className="text-foreground/70 hover:text-primary transition-colors">
                        pawsome.ky@gmail.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <div className="font-semibold text-foreground mb-1">Phone & WhatsApp</div>
                      <a href="tel:+13459162281" className="text-foreground/70 hover:text-primary transition-colors block mb-1">
                        +1 (345) 916-2281
                      </a>
                      <a 
                        href="https://wa.me/13459162281" 
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:text-primary/80 transition-colors"
                      >
                        Message on WhatsApp →
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <div className="font-semibold text-foreground mb-1">Mailing Address</div>
                      <p className="text-foreground/70">
                        PO Box KY1-1301<br />
                        West Bay, Grand Cayman<br />
                        Cayman Islands
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <div className="font-semibold text-foreground mb-1">Response Time</div>
                      <p className="text-foreground/70">
                        We respond within 48 hours<br />
                        24/7 support for active travel cases
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-primary/5 rounded-xl p-6 border border-primary/20">
                <h4 className="font-bold text-foreground mb-2">Around-the-Clock Support</h4>
                <p className="text-sm text-foreground/70">
                  Once your pet's journey begins, we're available 24/7 to support you every step of the way.
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="bg-muted/30 rounded-2xl p-8 md:p-10 border border-border">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <Label htmlFor="name" className="text-foreground mb-2 block">
                      Your Name *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Doe"
                      required
                      className="bg-white"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-foreground mb-2 block">
                      Email Address *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@example.com"
                      required
                      className="bg-white"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <Label htmlFor="phone" className="text-foreground mb-2 block">
                      Phone Number *
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+1 (234) 567-890"
                      required
                      className="bg-white"
                    />
                  </div>

                  <div>
                    <Label htmlFor="relocating-from" className="text-foreground mb-2 block">
                      Relocating From *
                    </Label>
                    <Input
                      id="relocating-from"
                      name="relocatingFrom"
                      value={formData.relocatingFrom}
                      onChange={handleChange}
                      placeholder="e.g., United Kingdom, United States"
                      required
                      className="bg-white"
                    />
                  </div>

                  <div>
                    <Label htmlFor="relocating-to" className="text-foreground mb-2 block">
                      Relocating To *
                    </Label>
                    <Input
                      id="relocating-to"
                      name="relocatingTo"
                      value={formData.relocatingTo}
                      onChange={handleChange}
                      placeholder="e.g., Cayman Islands, Australia"
                      required
                      className="bg-white"
                    />
                  </div>
                </div>

                {/* Multiple Pets Section */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <Label className="text-foreground">
                      Pet Information *
                    </Label>
                    <Button
                      type="button"
                      onClick={addPet}
                      variant="outline"
                      size="sm"
                      className="gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Add Another Pet
                    </Button>
                  </div>

                  <div className="space-y-6">
                    {pets.map((pet, index) => (
                      <div
                        key={pet.id}
                        className="border border-border rounded-lg p-6 bg-white relative"
                      >
                        {/* Pet Header */}
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="font-semibold text-foreground">
                            Pet {index + 1}
                          </h4>
                          {pets.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removePet(pet.id)}
                              className="text-destructive hover:text-destructive/80 p-2 rounded-full hover:bg-destructive/10 transition-colors"
                              aria-label="Remove pet"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>

                        {/* Pet Details */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          <div>
                            <Label htmlFor={`pet-name-${pet.id}`} className="text-foreground/80 mb-1.5 block text-sm">
                              Pet Name
                            </Label>
                            <Input
                              id={`pet-name-${pet.id}`}
                              value={pet.name}
                              onChange={(e) => handlePetChange(pet.id, 'name', e.target.value)}
                              placeholder="e.g., Max, Luna"
                              className="bg-muted/30"
                            />
                          </div>
                          <div>
                            <Label htmlFor={`pet-type-${pet.id}`} className="text-foreground/80 mb-1.5 block text-sm">
                              Type/Breed *
                            </Label>
                            <Input
                              id={`pet-type-${pet.id}`}
                              value={pet.type}
                              onChange={(e) => handlePetChange(pet.id, 'type', e.target.value)}
                              placeholder="e.g., Golden Retriever, Persian Cat"
                              required
                              className="bg-muted/30"
                            />
                          </div>
                        </div>

                        {/* File Upload for this Pet */}
                        <div className="mt-4">
                          <Label className="text-foreground mb-3 block font-semibold">
                            Required Documents for {pet.name || `Pet ${index + 1}`}
                          </Label>
                          
                          <div className="space-y-3">
                            {/* MANDATORY DOCUMENTS */}
                            <div className="mb-4">
                              <p className="text-xs font-semibold text-primary uppercase mb-3">Mandatory Documents</p>
                              
                              {/* Full Body Photo */}
                              <div className="border border-border rounded-lg p-3 bg-muted/10 mb-3">
                                <div className="flex items-center justify-between mb-2">
                                  <Label className="text-foreground/90 text-sm font-medium">
                                    Full Body, Colour Photo
                                  </Label>
                                  <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">Required</span>
                                </div>
                                <input
                                  type="file"
                                  id={`pet-photo-${pet.id}`}
                                  accept="image/*"
                                  onChange={(e) => handlePetFileChange(pet.id, e)}
                                  className="hidden"
                                />
                                <label
                                  htmlFor={`pet-photo-${pet.id}`}
                                  className="flex items-center justify-center cursor-pointer border-2 border-dashed border-border rounded-md p-2.5 bg-white hover:border-primary/50 transition-colors"
                                >
                                  <ImageIcon className="w-4 h-4 text-primary mr-2" />
                                  <span className="text-xs text-foreground/70">
                                    Upload full body, colour photo
                                  </span>
                                </label>
                              </div>

                              {/* Rabies Vaccination */}
                              <div className="border border-border rounded-lg p-3 bg-muted/10 mb-3">
                                <div className="flex items-center justify-between mb-2">
                                  <Label className="text-foreground/90 text-sm font-medium">
                                    Rabies Vaccination Certificate
                                  </Label>
                                  <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">If applicable</span>
                                </div>
                                <input
                                  type="file"
                                  id={`rabies-cert-${pet.id}`}
                                  accept=".pdf,.doc,.docx,image/*"
                                  onChange={(e) => handlePetFileChange(pet.id, e)}
                                  className="hidden"
                                />
                                <label
                                  htmlFor={`rabies-cert-${pet.id}`}
                                  className="flex items-center justify-center cursor-pointer border-2 border-dashed border-border rounded-md p-2.5 bg-white hover:border-primary/50 transition-colors"
                                >
                                  <FileText className="w-4 h-4 text-primary mr-2" />
                                  <span className="text-xs text-foreground/70">
                                    Upload rabies vaccination certificate
                                  </span>
                                </label>
                              </div>

                              {/* Microchip Certificate */}
                              <div className="border border-border rounded-lg p-3 bg-muted/10 mb-3">
                                <div className="flex items-center justify-between mb-2">
                                  <Label className="text-foreground/90 text-sm font-medium">
                                    Microchip Implantation Certificate
                                  </Label>
                                  <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">If applicable</span>
                                </div>
                                <input
                                  type="file"
                                  id={`microchip-${pet.id}`}
                                  accept=".pdf,.doc,.docx,image/*"
                                  onChange={(e) => handlePetFileChange(pet.id, e)}
                                  className="hidden"
                                />
                                <label
                                  htmlFor={`microchip-${pet.id}`}
                                  className="flex items-center justify-center cursor-pointer border-2 border-dashed border-border rounded-md p-2.5 bg-white hover:border-primary/50 transition-colors"
                                >
                                  <FileText className="w-4 h-4 text-primary mr-2" />
                                  <span className="text-xs text-foreground/70">
                                    Upload microchip certificate or proof of implantation date
                                  </span>
                                </label>
                              </div>

                              {/* Routine Vaccination */}
                              <div className="border border-border rounded-lg p-3 bg-muted/10">
                                <div className="flex items-center justify-between mb-2">
                                  <Label className="text-foreground/90 text-sm font-medium">
                                    Routine Vaccination Certificates
                                  </Label>
                                  <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">Required</span>
                                </div>
                                <input
                                  type="file"
                                  id={`routine-vax-${pet.id}`}
                                  accept=".pdf,.doc,.docx,image/*"
                                  onChange={(e) => handlePetFileChange(pet.id, e)}
                                  className="hidden"
                                />
                                <label
                                  htmlFor={`routine-vax-${pet.id}`}
                                  className="flex items-center justify-center cursor-pointer border-2 border-dashed border-border rounded-md p-2.5 bg-white hover:border-primary/50 transition-colors"
                                >
                                  <FileText className="w-4 h-4 text-primary mr-2" />
                                  <span className="text-xs text-foreground/70">
                                    Upload routine vaccination certificates
                                  </span>
                                </label>
                              </div>
                            </div>

                            {/* OPTIONAL DOCUMENTS */}
                            <div className="mt-5">
                              <p className="text-xs font-semibold text-foreground/60 uppercase mb-3">Optional Documents</p>
                              
                              {/* Titre Test */}
                              <div className="border border-border rounded-lg p-3 bg-muted/5 mb-3">
                                <Label className="text-foreground/80 text-sm font-medium mb-2 block">
                                  Titre Test Results (if applicable)
                                </Label>
                                <input
                                  type="file"
                                  id={`titre-test-${pet.id}`}
                                  accept=".pdf,.doc,.docx,image/*"
                                  onChange={(e) => handlePetFileChange(pet.id, e)}
                                  className="hidden"
                                />
                                <label
                                  htmlFor={`titre-test-${pet.id}`}
                                  className="flex items-center justify-center cursor-pointer border-2 border-dashed border-border/60 rounded-md p-2 bg-white hover:border-primary/30 transition-colors"
                                >
                                  <FileText className="w-4 h-4 text-foreground/50 mr-2" />
                                  <span className="text-xs text-foreground/60">
                                    Upload titre test results
                                  </span>
                                </label>
                              </div>

                              {/* Previous Import Permits */}
                              <div className="border border-border rounded-lg p-3 bg-muted/5 mb-3">
                                <Label className="text-foreground/80 text-sm font-medium mb-2 block">
                                  Previous Import Permits (if applicable)
                                </Label>
                                <input
                                  type="file"
                                  id={`import-permits-${pet.id}`}
                                  accept=".pdf,.doc,.docx,image/*"
                                  onChange={(e) => handlePetFileChange(pet.id, e)}
                                  className="hidden"
                                />
                                <label
                                  htmlFor={`import-permits-${pet.id}`}
                                  className="flex items-center justify-center cursor-pointer border-2 border-dashed border-border/60 rounded-md p-2 bg-white hover:border-primary/30 transition-colors"
                                >
                                  <FileText className="w-4 h-4 text-foreground/50 mr-2" />
                                  <span className="text-xs text-foreground/60">
                                    Upload previous import permits
                                  </span>
                                </label>
                              </div>

                              {/* Previous Export Permits */}
                              <div className="border border-border rounded-lg p-3 bg-muted/5 mb-3">
                                <Label className="text-foreground/80 text-sm font-medium mb-2 block">
                                  Previous Export Permits (if applicable)
                                </Label>
                                <input
                                  type="file"
                                  id={`export-permits-${pet.id}`}
                                  accept=".pdf,.doc,.docx,image/*"
                                  onChange={(e) => handlePetFileChange(pet.id, e)}
                                  className="hidden"
                                />
                                <label
                                  htmlFor={`export-permits-${pet.id}`}
                                  className="flex items-center justify-center cursor-pointer border-2 border-dashed border-border/60 rounded-md p-2 bg-white hover:border-primary/30 transition-colors"
                                >
                                  <FileText className="w-4 h-4 text-foreground/50 mr-2" />
                                  <span className="text-xs text-foreground/60">
                                    Upload previous export permits
                                  </span>
                                </label>
                              </div>

                              {/* Pet Passport */}
                              <div className="border border-border rounded-lg p-3 bg-muted/5">
                                <Label className="text-foreground/80 text-sm font-medium mb-2 block">
                                  Cayman Islands Pet Passport Photo Page (if applicable)
                                </Label>
                                <input
                                  type="file"
                                  id={`pet-passport-${pet.id}`}
                                  accept=".pdf,.doc,.docx,image/*"
                                  onChange={(e) => handlePetFileChange(pet.id, e)}
                                  className="hidden"
                                />
                                <label
                                  htmlFor={`pet-passport-${pet.id}`}
                                  className="flex items-center justify-center cursor-pointer border-2 border-dashed border-border/60 rounded-md p-2 bg-white hover:border-primary/30 transition-colors"
                                >
                                  <FileText className="w-4 h-4 text-foreground/50 mr-2" />
                                  <span className="text-xs text-foreground/60">
                                    Upload pet passport photo page
                                  </span>
                                </label>
                              </div>
                            </div>
                          </div>

                          {/* Files for this pet */}
                          {pet.files.length > 0 && (
                            <div className="mt-4">
                              <Label className="text-foreground/80 mb-2 block text-sm">
                                Uploaded Files ({pet.files.length})
                              </Label>
                              <div className="space-y-2">
                                {pet.files.map((file, fileIndex) => (
                                  <div
                                    key={fileIndex}
                                    className="flex items-center justify-between bg-white p-3 rounded border border-border"
                                  >
                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                      {file.type.startsWith('image/') ? (
                                        <ImageIcon className="w-4 h-4 text-primary flex-shrink-0" />
                                      ) : (
                                        <FileText className="w-4 h-4 text-primary flex-shrink-0" />
                                      )}
                                      <div className="min-w-0 flex-1">
                                        <p className="text-xs font-medium text-foreground truncate">
                                          {file.name}
                                        </p>
                                        <p className="text-xs text-foreground/50">
                                          {(file.size / (1024 * 1024)).toFixed(2)} MB
                                        </p>
                                      </div>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => removePetFile(pet.id, fileIndex)}
                                      className="text-destructive hover:text-destructive/80 p-1"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mb-6">
                  <Label htmlFor="message" className="text-foreground mb-2 block">
                    Additional Details
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your pet, travel dates, any special requirements..."
                    rows={5}
                    className="bg-white resize-none"
                  />
                </div>

                {/* Upload Progress */}
                {uploading && uploadProgress > 0 && (
                  <div className="mb-6">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-foreground/70">Uploading...</span>
                      <span className="text-primary font-medium">{uploadProgress}%</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                      <div
                        className="bg-primary h-full transition-all duration-300 ease-out"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Consultation Fee Acknowledgment */}
                <div className="mb-6">
                  <div className="flex items-start gap-3 p-4 bg-amber-50 border-2 border-amber-200 rounded-xl">
                    <input
                      type="checkbox"
                      id="accept-fee"
                      name="acceptFee"
                      checked={formData.acceptFee}
                      onChange={(e) => setFormData({ ...formData, acceptFee: e.target.checked })}
                      required
                      className="mt-1 w-4 h-4 text-primary border-amber-400 rounded focus:ring-primary focus:ring-2 cursor-pointer"
                    />
                    <label htmlFor="accept-fee" className="text-sm text-amber-900 cursor-pointer select-none">
                      <span className="font-semibold">I acknowledge</span> that a <span className="font-bold">CI$50 consultation fee</span> will be invoiced after the consultation. This fee covers the initial assessment and personalized travel plan for my pet(s). *
                    </label>
                  </div>
                </div>

                <Button 
                  type="submit"
                  disabled={uploading || !formData.acceptFee}
                  className="w-full md:w-auto bg-primary hover:bg-primary/90 text-white px-8 group disabled:opacity-50 disabled:cursor-not-allowed"
                  size="lg"
                >
                  {uploading ? 'Submitting...' : 'Request Consultation'}
                  {!uploading && <Send className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />}
                </Button>

                <div className="mt-4 space-y-2">
                  <p className="text-sm text-foreground/60">
                    * Required fields. Initial consultation fee: CI$50. We respond within 48 hours.
                  </p>
                  <p className="text-sm text-accent font-medium">
                    📋 A bespoke quotation will be prepared after your consultation, tailored specifically to your pet's needs and travel requirements.
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
